import type { Range, Transcript, TranscriptSegment, TranscriptWord } from './types'

/** Match the transcript to the ordered excerpts used by the visible preview. */
export function transcriptForPreview(transcript: Transcript | null, ranges: Range[]): Transcript | null {
  if (!transcript) return null
  const segments: TranscriptSegment[] = []
  let clipOffset = 0

  for (const range of ranges) {
    if (!Number.isFinite(range.start) || !Number.isFinite(range.end) || range.end <= range.start) continue
    for (const segment of transcript.segments || []) {
      if (segment.end <= range.start || segment.start >= range.end) continue
      const speaker = segment.speaker ? { speaker: segment.speaker, speaker_label: segment.speaker_label, speaker_name: segment.speaker_name } : {}
      if (segment.start >= range.start - 0.02 && segment.end <= range.end + 0.02 && segment.text.trim()) {
        // The provider's word list can omit spoken words. Keep its complete sentence
        // when the entire sentence is audible in this excerpt.
        segments.push({ start: clipOffset + Math.max(0, segment.start - range.start), end: clipOffset + Math.min(range.end - range.start, segment.end - range.start), text: segment.text, alignment_status: segment.alignment_status, words: segment.words?.map((word) => ({ ...word, start: clipOffset + Math.max(0, word.start - range.start), end: clipOffset + Math.min(range.end - range.start, word.end - range.start) })).filter((word) => word.end > word.start), ...speaker })
        continue
      }
      if (segment.alignment_status === 'needs_alignment') continue
      const sourceWords = segment.words?.length ? segment.words : transcript.words?.filter(
        (word) => word.end > segment.start && word.start < segment.end,
      )
      if (sourceWords?.length) {
        // A cut can split a source segment. Include only complete words heard in this excerpt.
        const words: TranscriptWord[] = sourceWords.filter((word) =>
          word.w.trim() && word.start >= range.start - 0.02 && word.end <= range.end + 0.02,
        ).map((word) => ({
          ...word,
          start: clipOffset + Math.max(0, word.start - range.start),
          end: clipOffset + Math.min(range.end - range.start, word.end - range.start),
        })).filter((word) => word.end > word.start)
        if (words.length) segments.push({ start: words[0].start, end: words[words.length - 1].end, text: words.map((word) => word.w).join(' '), words, ...speaker })
      }
    }
    clipOffset += range.end - range.start
  }

  return { segments }
}
